import { action, observable, makeObservable, reaction } from 'mobx';
import { ChannelEditForm } from 'modules/channel/forms';
import { ChannelStore } from 'modules/channel/stores';
import { BaseViewStore } from 'core/stores';
import {
	translate,
	isSome,
	notificationError,
	isSuperAdminOrganization,
	formatDate,
	isCurrentDateGreater,
	convertTimeToUTC,
} from 'core/utils';
import { channelType, sourceOfChange, sellerCloudChannels, carrierTypes } from 'common/constants';
import { ApplicationStore } from 'platform/application/stores';
import { LookupStore } from 'modules/lookup/stores';
import { CarrierStore } from 'modules/carrier/stores';
import { lookupRouteName, carrierChannelTypeEnum } from 'common/constants';
import { BaasicDropdownViewStore } from 'common/stores';
import { localStorageHandler } from 'core/services';
import { CredentialsValidationStore } from 'modules/credentials-validation/stores';
import { UpdateInventoryStore } from 'modules/product/stores';
import { RestockDate } from 'modules/restock-date/pages';
import { CustomLabel, WarehouseCodes, AmazonTemplates } from '../pages';
import _ from 'lodash';
import moment from 'moment';
import UpdateInventoryLog from 'modules/product/pages/UpdateInventoryLog';

class ChannelEditViewStore extends BaseViewStore {
	@observable page = 1;
	@observable infiniteSearchPhrase = null;
	@observable scheduledRestockDate = null;
	buyShippingDropdowns = [];
	convertWarehouses = [];
	amazonTemplatesModel = {
		amazonTemplates: [],
		defaultPrimeTemplate: '',
	};
	warehouses = [];
	isEdit = false;
	channel;
	amazonOAuthStateHash = null;
	oAuthDate = null;
	form = new ChannelEditForm({
		onSuccess: async () => {
			await this.createOrUpdateChannel();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.listStore = new ChannelStore(rootStore);
		this.credentialsValidationStore = new CredentialsValidationStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.carrierStore = new CarrierStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.isEdit = isSome(rootStore.routerStore.routerState.params.id);
		this.isSuperAdmin = isSuperAdminOrganization(rootStore);
		this.initialize();
	}

	@action.bound
	async initialize() {
		this.suspend();
		if (this.isEdit) await this.getChannel();
		await Promise.all([this.getCarrierTypes(), this.getWarehouses(), this.initializeDropdowns()]);
		this.setFieldRules();
		this.initializeReactions();
		this.handleBuyShippingCarriers();
		this.oAuthDate = new Date().toISOString();
		const md5 = require('md5');
		this.amazonOAuthStateHash = md5(this.rootStore.app.baasic.userHandler.user.user.id + this.oAuthDate);
		this.resume();
	}

	@action.bound
	initializeDropdowns = async () => {
		if (this.isSuperAdmin) {
			this.sellerDropdownStore = new BaasicDropdownViewStore();
			await this.sellerDropdownStore.initialize({
				field: this.form.$('sellerId'),
				value: this.isEdit ? this.channel.seller : localStorageHandler.get('selectedSeller'),
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				filterProperties: {
					sellerOnly: true,
				},
				onChangeCallback: this.clearData,
			});
			this.form.$('sellerId').set('rules', 'required');
		}
		_.each(
			['sellerCloudDropdownStore', 'channelTypeDropdownStore', 'timezoneDropdownStore', 'carrierChannelDropdownStore'],
			dropdownStore => {
				this[dropdownStore] = new BaasicDropdownViewStore();
			}
		);
		await Promise.all([
			this.sellerCloudDropdownStore.initialize({
				field: this.form.$('channelIds'),
				isMulti: true,
				value:
					_.get(this, 'channel.channelType.name') === channelType.sellerCloud
						? _.filter(sellerCloudChannels, sellerCloudChannel => {
								return this.channel.sellerCloudChannels.includes(sellerCloudChannel.value);
						  })
						: null,
				staticOptions: sellerCloudChannels,
			}),
			this.channelTypeDropdownStore.initialize({
				field: this.form.$('channelTypeId'),
				value: _.get(this, 'channel.channelType'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.channelType,
				onChangeCallback: async option => {
					await this.onChannelTypeChange(option);
				},
				initCallback: async () => {
					await this.onChannelTypeInitialize();
				},
			}),
			this.timezoneDropdownStore.initialize({
				field: this.form.$('timezoneId'),
				value: _.get(this.channel, 'timezone'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.timezone,
				itemTemplate: {
					value: 'id',
					label: 'abrv',
				},
			}),
			this.carrierChannelDropdownStore.initialize({
				field: this.form.$('carrierChannelIds'),
				value: this.getRequiredCarriers(),
				store: new CarrierStore(this.rootStore),
				filterProperties: {
					sellerIds: _.get(this.sellerDropdownStore, 'value') ? [this.sellerDropdownStore.value.value] : null,
					embed: ['carrierType'],
				},
				isMulti: true,
				fetchInitialValues: !this.isSuperAdmin || _.get(this.sellerDropdownStore, 'value') != null,
				onChangeCallback: () => {
					this.removeBuyShippingCarrier();
					_.forEach(this.buyShippingDropdowns, dropdown => {
						dropdown.reload();
					});
				},
			}),
		]);
	};

	@action.bound
	onChannelTypeChange = async option => {
		if (option.label === channelType.goFlow && !this.goFlowStoreDropdownStore) {
			await this.initializeGoFlowDropdowns(false);
		} else if (option.label === channelType.sellerCloud && !this.sellerCloudCompanyDropdownStore) {
			await this.initializeSellerCloudDropdowns(false);
		}
		this.handleBuyShippingCarriers();
	};

	@action.bound
	onChannelTypeInitialize = async () => {
		if (_.get(this.channelTypeDropdownStore, 'value.label') === channelType.goFlow) {
			await this.initializeGoFlowDropdowns();
		} else if (_.get(this.channelTypeDropdownStore, 'value.label') === channelType.sellerCloud) {
			await this.initializeSellerCloudDropdowns();
		}
	};

	@action.bound
	initializeReactions = () => {
		reaction(
			() => this.form.$('useBuyShippingForPrime').value || this.form.$('useBuyShippingForNonPrime').value,
			() => {
				const rules =
					this.form.$('useBuyShippingForPrime').value || this.form.$('useBuyShippingForNonPrime').value
						? 'required'
						: '';
				_.forEach([carrierTypes.fedex, carrierTypes.ups], carrierType => {
					if (this.form.has(`${carrierType.toLowerCase()}BuyShippingCarrier`)) {
						this.form.$(`${carrierType.toLowerCase()}BuyShippingCarrier`).set('rules', rules);
						this.form.$(`${carrierType.toLowerCase()}BuyShippingCarrier`).validate();
					}
				});
			}
		);
		reaction(
			() => _.get(this, 'channelTypeDropdownStore.value'),
			() => {
				this.setFieldRules();
				this.form.$('useBuyShippingForPrime').value =
					_.get(this.channelTypeDropdownStore, 'value.label') === channelType.amazon;
			}
		);
	};

	getRequiredCarriers = () => {
		if (!_.get(this.channel, 'carrierChannels')) return null;
		const selectedCarriers = [];
		_.forEach(this.channel.carrierChannels, item => {
			if (item.carrierChannelType == carrierChannelTypeEnum.requiredCarrier) {
				selectedCarriers.push(item.carrier);
			}
		});
		return selectedCarriers;
	};

	@action.bound
	getGoFlowStores = async () => {
		try {
			const { apiKey, subdomain } = this.form.values();
			await this.goFlowStoreDropdownStore.updateFilter({ apiKey, subdomain });
			if (_.get(this.goFlowStoreDropdownStore, 'store.response.item') === null)
				this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.STORESERROR']);
			else this.rootStore.notificationStore.success(['MESSAGES.CHANNEL.STORESSUCCESS']);
		} catch (err) {
			this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.STORESERROR']);
		}
	};

	@action.bound
	getSellerCloudCompanies = async () => {
		try {
			const { userName, password, teamName } = this.form.values();
			await this.sellerCloudCompanyDropdownStore.updateFilter({ userName, password, teamName });
			if (_.get(this.sellerCloudCompanyDropdownStore, 'store.response.item')) {
				if (this.sellerCloudCompanyDropdownStore.store.response.item.length > 0) {
					this.rootStore.notificationStore.success(['MESSAGES.CHANNEL.COMPANYSUCCESS']);
				} else {
					this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.COMPANYNORESULTS']);
				}
			} else {
				this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.COMPANYERROR']);
			}
		} catch (err) {
			this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.COMPANYERROR']);
		}
	};

	@action.bound
	validateCredentials = async () => {
		try {
			let data = this.form.values();
			await this.credentialsValidationStore.validateChannel({
				channelTypeName: _.get(this.channelTypeDropdownStore, 'value.label'),
				...data,
			});
			this.rootStore.notificationStore.success(['MESSAGES.CHANNEL.CONNECTIONVALID']);
		} catch (err) {
			this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.CONNECTIONINVALID']);
		}
	};

	@action.bound
	clearData = async () => {
		await this.clearCarrierData();
		await this.getWarehouses();
	};

	@action.bound
	clearCarrierData = async () => {
		if (!this.isEdit) {
			this.clearBuyShippingCarrierFields();
			if (this.carrierChannelDropdownStore) {
				this.carrierChannelDropdownStore.setValue([]);
				this.carrierChannelDropdownStore.filter.sellerIds = this.sellerDropdownStore.value
					? [this.sellerDropdownStore.value.value]
					: null;
				await this.carrierChannelDropdownStore.reload();
			}
		}
	};

	@action.bound
	getChannel = async () => {
		const locale = window.navigator.userLanguage || window.navigator.language;
		moment.locale(locale);
		const localeData = moment.localeData();
		this.datePickerFormat = localeData.longDateFormat('L');
		this.channel = await this.listStore.getChannel(this.rootStore.routerStore.routerState.params.id, {
			embed: [
				'channelType',
				'seller',
				'timezone',
				'seller',
				'carrier',
				'carrierChannels',
				'carrierChannels.carrier',
				'carrierChannels.carrier.carrierType',
				'productInventoryRestockDate',
				'customFieldInputs',
				'customFieldInputs.customFieldInputVariables',
				'customFieldInputs.customFieldInputVariables.customFieldVariable',
			],
		});
		this.selectedItem = this.channel;
		if (
			_.get(this, 'channel.productInventoryRestockDate.restockDate') != null &&
			_.get(this, 'channel.productInventoryRestockDate.scheduledRestockDateTime') != null &&
			!isCurrentDateGreater(this.channel.productInventoryRestockDate.restockDate)
		) {
			this.scheduledRestockDate = formatDate(
				this.channel.productInventoryRestockDate.scheduledRestockDateTime,
				true,
				'LLL'
			);
		}
		const { carrierChannels, ...other } = this.listStore.item; // dont update carrierChannels because it breaks multi-select validation
		this.form.updateForm(other, true);
		if (this.channel.channelType.name === channelType.amazonVendor) {
			this.form.$('useSandbox').value = this.channel.useSandbox === 'true'; // Because it comes as string from Json updateForm doesn't work.
		}
		if (this.channel.channelType.name === channelType.goFlow) {
			this.form.$('checkSplit').value = this.channel.checkSplit === 'true';
		}
		if (this.channel.lastSync) {
			const localDateTime = moment.utc(this.channel.lastSync).tz(moment.tz.guess());
			this.form.$('lastSyncDate').set('value', localDateTime.format('MM-DD-YYYY'));
			this.form.$('lastSyncTime').set('value', localDateTime.format('HH:mm:ss'));
		}
	};

	onLastSyncChange = () => {
		this.form.$('lastSyncDate').validate({ showErrors: true });
		this.form.$('lastSyncTime').validate({ showErrors: true });
	};

	@action.bound
	initializeSellerCloudDropdowns = async (fetchInitialValues = true) => {
		const { userName, password, teamName } = this.form.values();
		this.sellerCloudCompanyDropdownStore = new BaasicDropdownViewStore();
		await this.sellerCloudCompanyDropdownStore.initialize({
			field: this.form.$('companyIds'),
			value: _.get(this.channel, 'companies') ? JSON.parse(this.channel.companies) : null,
			store: new ChannelStore(this.rootStore),
			fetchFunc: 'getSellerCloudCompanies',
			fetchInitialValues: fetchInitialValues,
			isMulti: true,
			itemTemplate: {
				value: 'id',
				label: 'companyName',
			},
			filterProperties: {
				userName,
				password,
				teamName,
			},
			fetchAll: true,
		});
	};

	@action.bound
	initializeGoFlowDropdowns = async (fetchInitialValues = true) => {
		const { apiKey, subdomain } = this.form.values();
		this.goFlowStoreDropdownStore = new BaasicDropdownViewStore();
		await this.goFlowStoreDropdownStore.initialize({
			field: this.form.$('storeIds'),
			value: _.get(this.channel, 'stores') ? JSON.parse(this.channel.stores) : null,
			store: new ChannelStore(this.rootStore),
			fetchFunc: 'getGoFlowStores',
			fetchInitialValues: fetchInitialValues,
			isMulti: true,
			filterProperties: {
				apiKey,
				subdomain,
			},
			fetchAll: true,
		});
	};

	@action.bound
	getWarehouses = async () => {
		this.warehouses = (await this.applicationStore.find({
			rpp: 100,
			page: 1,
			isOrganizationActive: true,
			warehouseOnly: true,
			sellerWarehousesOnlyOrganizationId: this.isSuperAdmin ? _.get(this.sellerDropdownStore, 'value.value') : null,
			includeOnlySellerWarehouses: true,
		})).item;
		await this.prepareData();
	};

	@action.bound
	prepareData = async () => {
		this.convertWarehouses = [];
		this.amazonTemplatesModel = {
			amazonTemplates: [],
			defaultPrimeTemplate: '',
		};
		_.each(this.warehouses, warehouse => {
			this.convertWarehouses.push({
				warehouseName: warehouse.identifier,
				name: null, // Warehouse Code.
				warehouseId: warehouse.id,
			});
		});
	};

	getChannelCredentials = formValues => {
		let channelCredentials = {};

		switch (true) {
			case this.channelTypeDropdownStore.value.label === channelType.goFlow:
				channelCredentials = {
					apiKey: formValues.apiKey,
					subdomain: formValues.subdomain,
					checkSplit: formValues.checkSplit,
				};
				if (this.goFlowStoreDropdownStore.value && this.goFlowStoreDropdownStore.value.length > 0) {
					const stores = this.goFlowStoreDropdownStore.value.map(option => {
						return { id: option.value, name: option.label };
					});
					channelCredentials.stores = JSON.stringify(stores);
				}
				break;

			case this.channelTypeDropdownStore.value.label === channelType.walmart:
				channelCredentials = {
					apiKey: formValues.apiKey,
					apiSecret: formValues.apiSecret,
				};
				break;

			case this.channelTypeDropdownStore.value.label === channelType.amazon ||
				this.channelTypeDropdownStore.value.label === channelType.amazonVendor:
				channelCredentials = {
					amazonSellerId: formValues.amazonSellerId,
					marketplaceId: formValues.marketplaceId,
					refreshToken: formValues.refreshToken,
					sellingPartyId: formValues.sellingPartyId,
					useSandbox: formValues.useSandbox,
				};
				break;

			case this.channelTypeDropdownStore.value.label === channelType.wooCommerce:
				channelCredentials = {
					url: formValues.url,
					consumerKey: formValues.consumerKey,
					consumerSecret: formValues.consumerSecret,
				};
				break;

			case this.channelTypeDropdownStore.value.label === channelType.mirakl:
				channelCredentials = {
					url: formValues.url,
					shopId: formValues.shopId,
					apiKey: formValues.apiKey,
				};
				break;

			case this.channelTypeDropdownStore.value.label === channelType.shopify:
				channelCredentials = {
					url: formValues.url,
					accessToken: formValues.accessToken,
				};
				break;

			case this.channelTypeDropdownStore.value.label === channelType.sellerCloud:
				channelCredentials = {
					userName: formValues.userName,
					password: formValues.password,
					teamName: formValues.teamName,
					url: formValues.url,
				};
				if (this.sellerCloudDropdownStore.value && this.sellerCloudDropdownStore.value.length > 0) {
					channelCredentials.sellerCloudChannels = this.sellerCloudDropdownStore.value
						.map(item => String(item.value))
						.join(',');
				}
				if (this.sellerCloudCompanyDropdownStore.value && this.sellerCloudCompanyDropdownStore.value.length > 0) {
					const companies = this.sellerCloudCompanyDropdownStore.value.map(option => {
						return { id: option.value, companyName: option.label };
					});
					channelCredentials.companies = JSON.stringify(companies);
				}
				break;

			case this.channelTypeDropdownStore.value.label === channelType.costco:
				channelCredentials = {
					apiKey: formValues.apiKey,
					partnerId: formValues.partnerId,
				};
				break;

			case [
				channelType.buybuyBaby,
				channelType.mattressFirm,
				channelType.homeDepot,
				channelType.overstock,
				channelType.wayfair,
				channelType.overstockCA,
				channelType.logicBroker,
			].includes(this.channelTypeDropdownStore.value.label):
				channelCredentials = {
					apiKey: formValues.apiKey,
					partnerId: formValues.partnerId,
				};
				break;
		}
		return channelCredentials;
	};

	@action.bound
	createOrUpdateChannel = async () => {
		this.suspend();
		try {
			let formValues = this.form.values();
			let carrierChannels = null;
			// For Amazon Vendor, there are no carriers.
			if (this.channelTypeDropdownStore.value.label !== channelType.amazonVendor) {
				carrierChannels = _.map(this.form.$('carrierChannelIds').value, item => {
					return {
						carrierId: item,
						carrierChannelTypeEnum: carrierChannelTypeEnum.requiredCarrier,
					};
				});
				const buyShippingFields = _.filter(Object.getOwnPropertyNames(formValues), item => {
					return item.includes('BuyShippingCarrier');
				});
				_.forEach(buyShippingFields, fieldKey => {
					const buyShippingDropdown = this.buyShippingDropdowns.find(d => d.field.key === fieldKey);
					if (_.get(buyShippingDropdown, 'value') && buyShippingDropdown.value.label !== 'None') {
						carrierChannels.push({
							carrierId: formValues[fieldKey],
							carrierChannelType: carrierChannelTypeEnum.buyShipping,
						});
					}
				});
			}

			const channelCredentials = this.getChannelCredentials(formValues);

			if (
				this.channelTypeDropdownStore.value.label === channelType.sellerCloud &&
				(!channelCredentials.url || channelCredentials.teamName !== this.channel.teamName)
			) {
				try {
					channelCredentials.url = await this.listStore.getSellerCloudUrl(channelCredentials.teamName);
				}
				catch (error) {
					this.rootStore.notificationStore.error('MESSAGES.CHANNEL.MISSINGSELLERCLOUDURL');
					this.resume();
					return;
				}
			}

			let customLabelValues = null;
			// If data on custom label modal was changed
			if (this.customLabel) {
				customLabelValues = { ...this.customLabel };
			} else {
				if (this.isEdit) {
					customLabelValues = {
						sellerName: this.channel.sellerName,
						sellerPhone: this.channel.sellerPhone,
						useCustomReturnAddress: this.channel.useCustomReturnAddress,
						returnName: this.channel.returnName,
						returnPhone: this.channel.returnPhone,
						returnAddress: this.channel.returnAddress,
						returnCity: this.channel.returnCity,
						returnZip: this.channel.returnZip,
						returnState: this.channel.returnState,
						returnStateName: this.channel.returnStateName,
					};
				} else {
					// Custom Label data on insert is required
					this.rootStore.notificationStore.error('MESSAGES.CHANNEL.MISSINGREQUIREDVALUESLABEL');
					this.resume();
					return;
				}
			}

			if (this.isEdit) {
				await this.listStore.updateChannel(
					{
						id: this.rootStore.routerStore.routerState.params.id,
						name: formValues.name,
						timezoneId: formValues.timezoneId,
						channelTypeId: formValues.channelTypeId,
						carrierChannels: carrierChannels,
						shouldSync: this.channel.shouldSync,
						updateInventory: this.channel.updateInventory,
						shouldAcknowledgeOrders: formValues.shouldAcknowledgeOrders,
						shouldAutoCancel: formValues.shouldAutoCancel,
						useBuyShippingForPrime: formValues.useBuyShippingForPrime,
						useBuyShippingForNonPrime: formValues.useBuyShippingForNonPrime,
						useAmazonMli: formValues.useAmazonMli,
						lastSync:
							formValues.lastSyncDate && formValues.lastSyncTime
								? convertTimeToUTC(`${formValues.lastSyncDate} ${formValues.lastSyncTime}`)
								: null,
						checkBuyShippingSunday:
							this.channel.channelType.name === channelType.amazon ? formValues.checkBuyShippingSunday : false,
						sellerId: this.channel.sellerId,
						...channelCredentials,
						...customLabelValues,
					},
					sourceOfChange.updateChannel
				);
				this.rootStore.notificationStore.success(['LOOKUPUPDATE.CHANNEL', { name: this.form.$('name').value }]);
			} else {
				await this.listStore.createChannel(
					{
						name: formValues.name,
						channelTypeId: formValues.channelTypeId,
						carrierChannels: carrierChannels,
						convertWarehouses: this.convertWarehouses,
						timezoneId: formValues.timezoneId,
						shouldAcknowledgeOrders: formValues.shouldAcknowledgeOrders,
						shouldAutoCancel: formValues.shouldAutoCancel,
						useBuyShippingForPrime: formValues.useBuyShippingForPrime,
						useBuyShippingForNonPrime: formValues.useBuyShippingForNonPrime,
						useAmazonMli: formValues.useAmazonMli,
						checkBuyShippingSunday:
							this.channelTypeDropdownStore.value.label === channelType.amazon
								? formValues.checkBuyShippingSunday
								: false,
						sellerId: this.isSuperAdmin ? formValues.sellerId : null,
						sellerName: formValues.sellerName,
						sellerPhone: formValues.sellerPhone,
						defaultPrimeTemplate:
							this.channelTypeDropdownStore.value.label === channelType.amazon ? formValues.defaultPrimeTemplate : null,
						...channelCredentials,
						...customLabelValues,
						...this.amazonTemplatesModel,
					},
					sourceOfChange.createChannel
				);
				this.rootStore.notificationStore.success(['LOOKUPCREATE.CHANNEL', { name: this.form.$('name').value }]);
			}
			this.rootStore.routerStore.goTo('master.app.main.channel.list');
			if (
				this.isEdit &&
				this.channelTypeDropdownStore.value.label === channelType.amazon &&
				this.form.$('useAmazonMli').value == false &&
				this.form.$('useAmazonMli').value != this.channel.useAmazonMli
			) {
				const notificationId = this.rootStore.notificationStore.load(
					translate(['CHANNEL.STARTEDAMAZONMLIRESET', { name: this.channel.name }])
				);
				const result = await new UpdateInventoryStore(this.rootStore).updateInventoryForChannel(
					this.channel.id,
					sourceOfChange.channelEditUpdateInventoryLog,
					true
				);
				this.rootStore.notificationStore.dismiss(notificationId);
				if (result) {
					this.rootStore.notificationStore.success(['CHANNEL.MLIINVENTORYRESETSUCCESS', { name: this.channel.name }]);
				} else {
					this.rootStore.notificationStore.error(['CHANNEL.MLIINVENTORYRESETFAILED', { name: this.channel.name }]);
				}
			}
		} catch (err) {
			if (err.statusCode === 500) {
				if (err.data.details === 'MESSAGES.CHANNEL.DUPLICATECREDENTIALS') {
					const errorMessage = translate(['MESSAGES.CHANNEL.DUPLICATECREDENTIALS']);
					this.rootStore.notificationStore.error(errorMessage);
				}
			} else {
				if (err.statusCode === 400) {
					err.data.errorMessage = translate('MESSAGES.CHANNEL.EXISTS');
					if (err.data.exceptionMessage === 'MESSAGES.CHANNEL.DUPLICATECREDENTIALS') {
						err.data.errorMessage = translate('MESSAGES.CHANNEL.DUPLICATECREDENTIALS');
					}
				}
				this.rootStore.notificationStore.error(err.data.errorMessage);
			}
		}
		this.resume();
	};

	@action.bound
	async getCarrierTypes() {
		await this.lookupStore.find({ page: 1, rpp: 100 }, lookupRouteName.carrierType);
		this.carrierTypes = this.lookupStore.data;
	}

	@action.bound
	setSellingPartnerAPIRules() {
		this.form.$('amazonSellerId').set('rules', 'required');
		this.form.$('marketplaceId').set('rules', 'required');
		this.form.$('refreshToken').set('rules', 'required');
	}

	@action.bound
	handleBuyShippingCarriers = () => {
		if (_.get(this, 'channelTypeDropdownStore.value.label') === channelType.amazon) {
			this.addBuyShippingCarrierFields();
			this.removeBuyShippingCarrier();
			if (this.isEdit) {
				this.updateBuyShippingCarrierFields(
					_.filter(this.channel.carrierChannels, item => {
						return item.carrierChannelType == carrierChannelTypeEnum.buyShipping;
					})
				);

				_.forEach([carrierTypes.fedex, carrierTypes.ups], carrierType => {
					const dropdown = this.buyShippingDropdowns.find(
						d => d.field.name === `${carrierType.toLowerCase()}BuyShippingCarrier`
					);
					if (dropdown && !dropdown.value) {
						dropdown.setValue({ name: 'None' });
					}
				});
			}
		} else {
			this.removeBuyShippingCarrierFields();
		}
	};

	@action.bound
	setFieldRules() {
		if (!this.channelTypeDropdownStore.value) return;
		this.resetFieldRules();
		if ([channelType.amazonVendor, channelType.amazon].includes(this.channelTypeDropdownStore.value.label)) {
			this.setSellingPartnerAPIRules();
		}

		if (this.channelTypeDropdownStore.value.label === channelType.goFlow) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('subdomain').set('rules', 'required');
			this.form.$('storeIds').set('rules', 'required');
		}

		if (this.channelTypeDropdownStore.value.label === channelType.amazonVendor) {
			this.form.$('sellingPartyId').set('rules', 'required');
			this.form.$('carrierChannelIds').set('rules', '');
		} else {
			this.form.$('sellingPartyId').set('rules', '');
			this.form.$('carrierChannelIds').set('rules', 'required');
		}

		if (
			[channelType.costco, channelType.overstockCA, channelType.logicBroker].includes(
				this.channelTypeDropdownStore.value.label
			)
		) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('partnerId').set('rules', 'required');
		}
		if (this.channelTypeDropdownStore.value.label === channelType.overstockCA) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('partnerId').set('rules', 'required');
		}
		if (this.channelTypeDropdownStore.value.label === channelType.mirakl) {
			this.form.$('url').set('rules', 'required');
			this.form.$('shopId').set('rules', 'required');
			this.form.$('apiKey').set('rules', 'required');
		}
		if (this.channelTypeDropdownStore.value.label === channelType.shopify) {
			this.form.$('url').set('rules', 'required');
			this.form.$('accessToken').set('rules', 'required');
		}
		if (this.channelTypeDropdownStore.value.label === channelType.sellerCloud) {
			this.form.$('teamName').set('rules', 'required');
			this.form.$('userName').set('rules', 'required');
			this.form.$('password').set('rules', 'required');
			this.form.$('channelIds').set('rules', 'required');
			this.form.$('companyIds').set('rules', 'required');
		}
		if (
			[
				channelType.buybuyBaby,
				channelType.mattressFirm,
				channelType.homeDepot,
				channelType.overstock,
				channelType.wayfair,
			].includes(this.channelTypeDropdownStore.value.label)
		) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('partnerId').set('rules', 'required');
		}
		if (this.channelTypeDropdownStore.value.label === channelType.walmart) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('apiSecret').set('rules', 'required');
		}
	}

	@action.bound
	resetFieldRules() {
		this.form.$('apiKey').set('rules', '');
		this.form.$('sellingPartyId').set('rules', '');
		this.form.$('partnerId').set('rules', '');
		this.form.$('apiSecret').set('rules', '');
		this.form.$('refreshToken').set('rules', '');
		this.form.$('accessToken').set('rules', '');
		this.form.$('amazonSellerId').set('rules', '');
		this.form.$('marketplaceId').set('rules', '');
		this.form.$('url').set('rules', '');
		this.form.$('shopId').set('rules', '');
		this.form.$('teamName').set('rules', '');
		this.form.$('userName').set('rules', '');
		this.form.$('password').set('rules', '');
		this.form.$('channelIds').set('rules', '');
	}

	@action.bound
	addBuyShippingCarrierFields = () => {
		if (this.buyShippingDropdowns.length === 0) {
			_.each(this.carrierTypes, carrierType => {
				const fieldName = `${carrierType.abrv.toLowerCase()}BuyShippingCarrier`;
				this.form.add({
					name: fieldName,
					rules:
						[carrierTypes.fedex, carrierTypes.ups].includes(carrierType.name) &&
						(this.form.$('useBuyShippingForPrime').value || this.form.$('useBuyShippingForNonPrime').value)
							? 'required'
							: '',
					label: `CHANNEL.BUYSHIPPING.${carrierType.abrv.toUpperCase()}`,
				});
				const dropdownStore = new BaasicDropdownViewStore({
					field: this.form.$(fieldName),
					store: new CarrierStore(this.rootStore),
					filterProperties: {
						sellerIds: _.get(this.sellerDropdownStore, 'value') ? [this.sellerDropdownStore.value.value] : null,
						carrierTypeIds: [carrierType.id],
						embed: ['carrierType'],
					},
					filterCallback: options => {
						return this.filterBuyShippingCarriers(options, carrierType);
					},
				});
				this.buyShippingDropdowns.push(dropdownStore);
			});
		}
	};

	@action.bound
	filterBuyShippingCarriers = (options, carrierType) => {
		let filteredOptions = options.filter(option =>
			(this.carrierChannelDropdownStore.value ? this.carrierChannelDropdownStore.value : []).some(
				reqCarrier => reqCarrier.value === option.id
			)
		);
		if (carrierType.name === carrierTypes.ups || carrierType.name === carrierTypes.fedex) {
			filteredOptions.push({ name: 'None' });
		}
		return _.map(filteredOptions, option => {
			return {
				value: _.get(option, 'id'),
				label: _.get(option, 'name'),
				extra: option,
			};
		});
	};

	@action.bound
	updateBuyShippingCarrierFields = carrierChannels => {
		_.forEach(carrierChannels, carrierChannel => {
			const fieldName = `${carrierChannel.carrier.carrierType.abrv.toLowerCase()}BuyShippingCarrier`;
			const buyShippingDropdown = this.buyShippingDropdowns.find(d => d.field.name === fieldName);
			if (buyShippingDropdown) {
				buyShippingDropdown.setValue(carrierChannel.carrier);
			}
		});
	};

	@action.bound
	clearBuyShippingCarrierFields = () => {
		_.forEach(this.carrierTypes, carrierType => {
			const fieldName = `${carrierType.abrv.toLowerCase()}BuyShippingCarrier`;
			const buyShippingDropdown = this.buyShippingDropdowns.find(d => d.field.name === fieldName);
			if (buyShippingDropdown) {
				buyShippingDropdown.clear();
			}
		});
	};

	@action.bound
	removeBuyShippingCarrierFields = () => {
		_.forEach(this.carrierTypes, carrierType => {
			const fieldName = `${carrierType.abrv.toLowerCase()}BuyShippingCarrier`;
			const index = this.buyShippingDropdowns.findIndex(d => d.field.name === fieldName);
			if (this.form.has(fieldName) && index !== -1) {
				this.form.del(fieldName);
				this.buyShippingDropdowns.splice(index, 1);
			}
		});
	};

	@action.bound
	removeBuyShippingCarrier = () => {
		_.forEach(this.carrierTypes, carrierType => {
			const fieldName = `${carrierType.abrv.toLowerCase()}BuyShippingCarrier`;
			if (!this.form.has(fieldName) || !this.carrierChannelDropdownStore) return;
			const dropdown = this.buyShippingDropdowns.find(d => d.field.name === fieldName);
			const isRequiredCarrierSelected =
				this.carrierChannelDropdownStore.value &&
				this.carrierChannelDropdownStore.value.some(item => item.label === _.get(dropdown, 'value.label'));
			if (!isRequiredCarrierSelected && dropdown.value && dropdown.value.label !== 'None') {
				dropdown.clear();
			}
		});
	};

	@action.bound
	async onAuthorizeAmazon() {
		try {
			await this.listStore.authorizeAmazon(this.oAuthDate);
			this.openConfirmationModal(this.getAuthorizeConfirmModalProps());
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	}

	getAuthorizeConfirmModalProps = () => {
		return {
			onConfirm: async () => {
				// Get authorization results from Redis.
				try {
					var response = await this.listStore.getAuthorizationResultAmazon(this.amazonOAuthStateHash);
					if (isSome(response) && isSome(response.refreshToken) && isSome(response.sellerId)) {
						this.form.$('refreshToken').value = response.refreshToken;
						this.form.$('amazonSellerId').value = response.sellerId;
						this.rootStore.notificationStore.success(['OAUTH.AMAZON.SUCCESSFORMUPDATE']);
					} else {
						this.rootStore.notificationStore.error(['OAUTH.AMAZON.FAILGETREFRESHTOKEN']);
					}
				} catch (err) {
					if (err.statusCode === 404) {
						this.rootStore.notificationStore.error(['OAUTH.AMAZON.STATENOTFOUND']);
					} else {
						notificationError({ store: this.rootStore.notificationStore, err });
					}
				}
			},
			message: ['OAUTH.AMAZON.CONFIRMSUCCESSAUTH'],
		};
	};

	openEditRestockDateModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: this,
			component: RestockDate,
		});
	};

	openEditChannelWarehouseCodesModal = () => {
		if (this.isSuperAdmin && !this.sellerDropdownStore.value) {
			this.rootStore.notificationStore.warning(['CHANNEL.ORGANIZATIONREQUIRED']);
			return;
		}
		this.openModal({
			data: this,
			component: WarehouseCodes,
			className: 'ReactModal--lrg ReactModal--vertical',
		});
	};

	openEditChannelAmazonTemplatesModal = () => {
		if (this.isSuperAdmin && _.get(this.sellerDropdownStore, 'value.value') == null) {
			this.rootStore.notificationStore.warning(['CHANNEL.ORGANIZATIONREQUIRED']);
			return;
		}
		this.openModal({
			data: this,
			component: AmazonTemplates,
			className: 'ReactModal--lrg ReactModal--vertical',
		});
	};

	openCustomLabelModal = () => {
		this.openModal({
			data: this,
			component: CustomLabel,
			className: 'ReactModal--lrg-inter',
		});
	};

	openUpdateInventoryLogModal = () => {
		this.openModal({
			data: this,
			component: UpdateInventoryLog,
			className: 'ReactModal--lrg',
		});
	};
}

export default ChannelEditViewStore;



// WEBPACK FOOTER //
// ./src/modules/channel/stores/ChannelEditViewStore.js